import { useState } from 'react';
import { aiService } from '../services/aiService';
import { ExtractedMaintenanceData } from '../services/aiService';
import { useTranslation } from 'react-i18next';

interface MaintenanceAIHook {
  processMaintenanceInvoice: (imageBase64: string) => Promise<ExtractedMaintenanceData | null>;
  processMultipleImages: (imagesBase64: string[]) => Promise<ExtractedMaintenanceData | null>;
  isProcessing: boolean;
  error: string | null;
  setError: (error: string | null) => void;
}

export const useMaintenanceAI = (): MaintenanceAIHook => {
  const { t } = useTranslation();
  const [isProcessing, setIsProcessing] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const compressImage = async (imageBase64: string): Promise<string> => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      // Ensure we're working with a complete data URL
      img.src = imageBase64.startsWith('data:') ? imageBase64 : `data:image/jpeg;base64,${imageBase64}`;
      
      img.onload = () => {
        try {
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');
          if (!ctx) {
            reject(new Error('Could not get canvas context'));
            return;
          }

          // Calculate new dimensions while maintaining aspect ratio
          let width = img.width;
          let height = img.height;
          const maxDimension = 1600; // Maximum dimension for either width or height

          if (width > height && width > maxDimension) {
            height = (height * maxDimension) / width;
            width = maxDimension;
          } else if (height > maxDimension) {
            width = (width * maxDimension) / height;
            height = maxDimension;
          }

          // Ensure dimensions are integers
          width = Math.floor(width);
          height = Math.floor(height);

          canvas.width = width;
          canvas.height = height;

          // Clear canvas before drawing
          ctx.clearRect(0, 0, width, height);
          
          // Draw and compress image
          ctx.drawImage(img, 0, 0, width, height);
          
          // Get base64 with reduced quality
          const compressedBase64 = canvas.toDataURL('image/jpeg', 0.7);
          
          // Remove the data:image/jpeg;base64, prefix
          resolve(compressedBase64.split(',')[1]);
        } catch (error) {
          console.error('Error during image compression:', error);
          reject(error);
        }
      };

      img.onerror = (error) => {
        console.error('Error loading image:', error);
        reject(new Error('Failed to load image. Please ensure the image format is supported.'));
      };
    });
  };

  const processMaintenanceInvoice = async (imageBase64: string): Promise<ExtractedMaintenanceData | null> => {
    setIsProcessing(true);
    setError(null);

    const maxRetries = 3;
    let retryCount = 0;

    try {
      while (retryCount < maxRetries) {
        try {
          // Compress the image before sending to API
          const compressedImage = await compressImage(imageBase64);
          const extractedData = await aiService.extractMaintenanceDataFromInvoice(compressedImage);
        
          if (!extractedData) {
            throw new Error('No data extracted');
          }

          return extractedData;
        } catch (err) {
          console.error(`Error processing maintenance invoice (attempt ${retryCount + 1}/${maxRetries}):`, err);
          
          if (retryCount === maxRetries - 1) {
            // Last attempt failed
            setError(t('maintenance.errors.processingError'));
            return null;
          }
          
          // Wait before retrying (exponential backoff)
          await new Promise(resolve => setTimeout(resolve, Math.pow(2, retryCount) * 1000));
          retryCount++;
        }
      }

      return null;
    } finally {
      setIsProcessing(false);
    }
  };

  const processMultipleImages = async (imagesBase64: string[]): Promise<ExtractedMaintenanceData | null> => {
    setIsProcessing(true);
    setError(null);

    const maxRetries = 3;
    let retryCount = 0;

    try {
      while (retryCount < maxRetries) {
        try {
          console.log('Starting to process multiple images:', imagesBase64.length);
          
          // Compress all images first
          const compressedImages = await Promise.all(
            imagesBase64.map(async (img: string) => {
              try {
                return await compressImage(img);
              } catch (error) {
                console.error('Error compressing image:', error);
                throw error;
              }
            })
          );
          
          // Process first image to get base data
          console.log('Processing first image to get base data');
          const baseResult = await aiService.extractMaintenanceDataFromInvoice(compressedImages[0]);
          if (!baseResult) {
            console.error('Failed to process first image');
            throw new Error('Failed to process first image');
          }

          // Process remaining images for additional information
          const additionalResults = await Promise.all(
            compressedImages.slice(1).map(async (img: string, index: number) => {
              console.log(`Processing additional image ${index + 2}/${compressedImages.length}`);
              try {
                const result = await aiService.extractMaintenanceDataFromInvoice(img);
                console.log(`Additional image ${index + 2} processing result:`, result);
                return result;
              } catch (error) {
                console.error(`Error processing additional image ${index + 2}:`, error);
                return null;
              }
            })
          );

          // Combine all results, using base result as the foundation
          const results = [baseResult, ...additionalResults.filter((r): r is ExtractedMaintenanceData => r !== null)];

          console.log('All images processed, results:', results);

          // Initialize combined data with base result
          const combinedData: ExtractedMaintenanceData = {
            ...baseResult,
            detailedOperations: {
              parts: [...(baseResult.detailedOperations?.parts || [])],
              labor: [...(baseResult.detailedOperations?.labor || [])]
            }
          };

          // Ensure detailedOperations is always defined
          if (!combinedData.detailedOperations) {
            combinedData.detailedOperations = {
              parts: [],
              labor: []
            };
          }
          
          results.forEach((result) => {
            if (!result) return;
            
            // Validate and merge data
            if (result.type && ['revision', 'technical_control', 'repair'].includes(result.type)) {
              combinedData.type = result.type;
            }
            
            if (typeof result.costHT === 'number' && result.costHT > 0) {
              combinedData.costHT = result.costHT;
            }
            
            if (typeof result.costTTC === 'number' && result.costTTC > 0) {
              combinedData.costTTC = result.costTTC;
            }
            
            if (result.date) {
              combinedData.date = result.date;
            }
            
            if (result.description) {
              combinedData.description = result.description;
            }
            
            // Merge detailed operations
            if (result.detailedOperations?.parts) {
              const currentParts = combinedData.detailedOperations?.parts || [];
              combinedData.detailedOperations = {
                ...combinedData.detailedOperations,
                parts: [
                  ...currentParts,
                  ...result.detailedOperations.parts.filter((part) => part.name)
                ]
              };
            }
            
            if (result.detailedOperations?.labor) {
              const currentLabor = combinedData.detailedOperations?.labor || [];
              combinedData.detailedOperations = {
                ...combinedData.detailedOperations,
                labor: [
                  ...currentLabor,
                  ...result.detailedOperations.labor.filter((op) => op.description)
                ]
              };
            }
            
            // Vehicle info - take the most complete set
            if (result.vehicleInfo) {
              if (!combinedData.vehicleInfo || 
                  Object.keys(result.vehicleInfo).length > Object.keys(combinedData.vehicleInfo).length) {
                combinedData.vehicleInfo = result.vehicleInfo;
              }
            }
            
            // Service provider - only update if more complete information is found
            if (result.serviceProvider && (!combinedData.serviceProvider || 
                Object.keys(result.serviceProvider).length > Object.keys(combinedData.serviceProvider).length)) {
              combinedData.serviceProvider = result.serviceProvider;
            }
          });

          console.log('Combined data:', combinedData);
          
          // Validate combined data
          if (!combinedData.costHT && !combinedData.costTTC) {
            console.warn('No cost information could be extracted');
            throw new Error('No cost information could be extracted');
          }

          return combinedData;
        } catch (err) {
          console.error(`Error processing multiple images (attempt ${retryCount + 1}/${maxRetries}):`, err);
          
          if (retryCount === maxRetries - 1) {
            // Last attempt failed
            setError(t('maintenance.errors.processingError'));
            return null;
          }
          
          // Wait before retrying (exponential backoff)
          await new Promise(resolve => setTimeout(resolve, Math.pow(2, retryCount) * 1000));
          retryCount++;
        }
      }

      return null;
    } finally {
      setIsProcessing(false);
    }
  };

  return {
    processMaintenanceInvoice,
    processMultipleImages,
    isProcessing,
    error,
    setError
  };
};
